import type { BagSortOrder, ItemRefusalReason, ItemRequest } from "odyssey-shared";
import type { ChangedSlot } from "./wire.ts";

/**
 * The bag store's shapes (P2.8-T11 and P2.8-T14): what a view of the bag reads, what the senders send, and the pure
 * reading of a change into slots, apart from the store that keeps them.
 */

/**
 * One slot as the window draws it: its item's rules id, or null for an empty slot; its quantity, 0 for an empty slot;
 * and whether a request on it waits for the server's answer.
 */
export interface BagSlot {
  readonly item: string | null;
  readonly quantity: number;
  readonly pending: boolean;
}

/**
 * What the window reads: whether it is open; the bag's size as the last `bag` named it, or null before any; every
 * slot from 0, as many as the size or as the highest slot the server holds, whichever is more (a slot past the size is
 * kept and usable, ADR-091 decision 8); the slot picked up to be moved, null for none; the slot the latest move
 * still waiting for the server came from, null for none; and whether that move has waited `PENDING_SPINNER_DELAY_MS`
 * with no answer (`slow`, which the window draws as the kit's pending spinner, P2.8-T41); the sort's state; and the
 * slots whose stack is kept in place (P2.8-T49, ADR-125 decision 6), in ascending order, as the last `bagKept` named
 * them less any slot emptied since.
 */
export interface BagSnapshot {
  readonly open: boolean;
  readonly size: number | null;
  readonly slots: readonly BagSlot[];
  readonly kept: readonly number[];
  readonly selected: number | null;
  readonly moving: number | null;
  readonly slow: boolean;
  readonly sort: BagSortState;
}

/**
 * The sort as the window draws it (P2.8-T48, ADR-125 decision 3.6): the order of the sort waiting for the server, null
 * for none, which hatches every slot and makes the Sort button busy; whether it has waited `PENDING_SPINNER_DELAY_MS`
 * with no answer (`slow`, the kit's pending spinner); and the last order the page sent this session, null before any,
 * which Sort's menu checks.
 */
export interface BagSortState {
  readonly pending: BagSortOrder | null;
  readonly slow: boolean;
  readonly last: BagSortOrder | null;
}

/**
 * A request the server refused, as `itemRefused` names it.
 */
export interface BagRefusal {
  readonly request: ItemRequest;
  readonly slot: number;
  readonly reason: ItemRefusalReason;
}

/**
 * What a view of the bag reads: its snapshot and a way to hear it change. Both work unbound, as React calls them.
 */
export interface BagSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => BagSnapshot;
}

/**
 * Sends one `useItem`: the slot and the item the page expects there.
 *
 * @returns Whether it was sent.
 */
export type UseSender = (slot: number, item: string) => boolean;

/**
 * Sends one `bagMove`: the slot moved from and the slot moved onto.
 *
 * @returns Whether it was sent.
 */
export type MoveSender = (from: number, to: number) => boolean;

/**
 * Sends one `bagSplit`: the slot split.
 *
 * @returns Whether it was sent.
 */
export type SplitSender = (slot: number) => boolean;

/**
 * Sends one `destroyItem`: the slot, the item the page expects there and the count the player confirmed.
 *
 * @returns Whether it was sent.
 */
export type DestroySender = (slot: number, item: string, quantity: number) => boolean;

/**
 * Sends one `dropItem` (P2.8-T34): the slot, the item the page holds there and its whole count; true when it went.
 */
export type DropSender = (slot: number, item: string, quantity: number) => boolean;

/**
 * Sends one `bagSort`: the order.
 *
 * @returns Whether it was sent.
 */
export type SortSender = (by: BagSortOrder) => boolean;

/**
 * Sends one `bagKeep`: the slot and whether to keep its stack in place.
 *
 * @returns Whether it was sent.
 */
export type KeepSender = (slot: number, kept: boolean) => boolean;

/**
 * A request still waiting for the server that hatches more than its own slot, or names it as its own: a move, its
 * other slot the one moved onto; a split, the slot its new half lands in; a destroy or a drop (P2.8-T34), its own slot.
 */
export interface Waiting {
  readonly kind: "move" | "split" | "destroy" | "drop";
  readonly other: number;
}

/**
 * How long a slot stays pending with no answer, in ms. An answer normally comes within a tick or two of the commit;
 * the map server retries a failed write every second and holds four operations a character, so five seconds is past
 * any answer that is still coming. A late answer still lands: it is applied as any message is.
 */
export const PENDING_TIMEOUT_MS = 5000;

/**
 * Runs `run` once after `ms`, returning a way to cancel it.
 */
export type BagTimer = (run: () => void, ms: number) => () => void;

/**
 * The page's timer, on `setTimeout`.
 *
 * @param run What to run.
 * @param ms After how long.
 * @returns Cancels it.
 */
export const PAGE_TIMER: BagTimer = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/**
 * An empty slot.
 */
export const EMPTY_SLOT: BagSlot = { item: null, quantity: 0, pending: false };

/**
 * The bag before the server has said anything of it.
 */
export const NO_BAG: BagSnapshot = { open: false, size: null, slots: [], kept: [], selected: null, moving: null, slow: false, sort: { pending: null, slow: false, last: null } };

/**
 * The kept slots once a change has landed: a slot it emptied no longer holds a kept stack. Where the change moved a
 * kept stack the server's `bagKept` follows it at once with the new set (ADR-125 decision 6.4).
 *
 * @param kept The kept slots before.
 * @param slots The slots after the change.
 * @returns The kept slots that still hold an item, the same list when none was emptied.
 */
export function keptLeft(kept: readonly number[], slots: readonly BagSlot[]): readonly number[] {
  /**
   * Whether a slot still holds an item after the change.
   *
   * @param slot The slot.
   * @returns True when it does.
   */
  const holds = (slot: number): boolean => (slots[slot]?.item ?? null) !== null;
  return kept.every(holds) ? kept : kept.filter(holds);
}

/**
 * The bag once a sort is sent (ADR-125 decision 3.6): every slot hatched, nothing picked up, the order pending and the
 * last sent.
 *
 * @param bag The bag before.
 * @param by The order sent.
 * @returns The bag while the sort waits.
 */
export function sortSent(bag: BagSnapshot, by: BagSortOrder): BagSnapshot {
  return { ...bag, selected: null, sort: { pending: by, slow: false, last: by }, slots: bag.slots.map((each) => ({ ...each, pending: true })) };
}

/**
 * The bag once the sort waiting is answered, refused or given up: no order pending, and every slot's hatch cleared,
 * since nothing else can wait while a sort does.
 *
 * @param bag The bag before.
 * @param slots The slots after the answer.
 * @returns The bag.
 */
export function sortEnded(bag: BagSnapshot, slots: readonly BagSlot[]): BagSnapshot {
  return { ...bag, sort: { ...bag.sort, pending: null, slow: false }, slots: slots.map((each) => (each.pending ? { ...each, pending: false } : each)) };
}

/**
 * A slot kept picked up across a change: kept only while it still holds an item.
 *
 * @param slots The slots after the change.
 * @param slot The slot, or null.
 * @returns The slot, or null when it is now empty.
 */
export function held(slots: readonly BagSlot[], slot: number | null): number | null {
  return slot !== null && (slots[slot]?.item ?? null) !== null ? slot : null;
}

/**
 * A bag's slots after a change: each slot the change names holds what it says, the list growing to reach a slot past
 * its end. A slot keeps its pending mark unless the change is the answer to the page's own request, a use or a move.
 *
 * @param slots The slots before.
 * @param changed Each changed slot's new content.
 * @param answersUse Whether the change answers a request of the page's, which clears the pending mark of each slot it
 *   names; loot landing on a slot does not.
 * @returns The slots after.
 */
export function changedSlots(slots: readonly BagSlot[], changed: readonly ChangedSlot[], answersUse: boolean): readonly BagSlot[] {
  const length = changed.reduce((most, [slot]) => Math.max(most, slot + 1), slots.length);
  const next: BagSlot[] = Array.from({ length }, (_, index) => slots[index] ?? EMPTY_SLOT);
  for (const [slot, item, quantity] of changed) {
    const pending = !answersUse && item !== null && (next[slot] as BagSlot).pending;
    next[slot] = item === null ? EMPTY_SLOT : { item, quantity, pending };
  }
  return next;
}
